import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import Keycloak from 'keycloak-js';

import { ReactKeycloakProvider } from '@react-keycloak/web';

import { sessionsDestroy } from '@/services/Sessions';
import { SSO_LOGIN_ERROR_KEY, SSO_PENDING_KEY } from '@/assets/AppConstants';
import { useUserContext } from '../user';

import { KeycloakContext } from './context';
interface KeycloakProviderProps {
  children: ReactNode;
}

const KeycloakProvider = ({ children }: KeycloakProviderProps) => {
  const {
    userSession,
    logoutUser,
    handleLoginWithToken,
  } = useUserContext();

  const {
    realmId,
    authHost,
    external,
    authenticated,
  } = userSession;

  const [returningFromIdp] = useState(
    () => new URLSearchParams(window.location.search).has('code'),
  );
  const [ssoStarted, setSsoStarted] = useState(false);
  const ssoPending = ssoStarted || sessionStorage.getItem(SSO_PENDING_KEY) === '1';

  const hasSsoConfig = Boolean(realmId && authHost && external);
  const canKeycloak = hasSsoConfig && (authenticated || returningFromIdp || ssoPending);
  const staleSso = hasSsoConfig && !canKeycloak;

  useEffect(() => {
    if (hasSsoConfig && ssoPending && !ssoStarted) {
      setSsoStarted(true);
      sessionStorage.removeItem(SSO_PENDING_KEY);
    }
  }, [hasSsoConfig, ssoPending, ssoStarted]);

  useEffect(() => {
    if (staleSso) {
      logoutUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleSso]);

  const authClient = useMemo(() => (
    canKeycloak ? new Keycloak({
      url: authHost,
      realm: realmId,
      clientId: 'datanomik',
    }) : null
  ), [realmId, authHost, canKeycloak]);

  const handleToken = async ({ idToken, refreshToken, token }) => {
    if (idToken && external && !authenticated) {
      const loggedIn = await handleLoginWithToken({ idToken, refreshToken, token });

      if (!loggedIn) {
        // Close the Keycloak session too, so the next attempt starts from a clean
        // state instead of silently re-authenticating with the same account.
        localStorage.setItem(SSO_LOGIN_ERROR_KEY, 'true');
        sessionStorage.removeItem(SSO_PENDING_KEY);
        await authClient.logout({ redirectUri: `${window.location.origin}/login` });
      }
    }
  };

  const handleLoginKeycloak = async () => {
    if (canKeycloak) {
      await authClient.login();
    }
  };

  const handleLogoutKeycloak = async () => {
    await sessionsDestroy();
    sessionStorage.removeItem(SSO_PENDING_KEY);
    logoutUser();

    if (canKeycloak) {
      await authClient.logout(
        { redirectUri: window.location.href.replace('#survey-1', '') },
      );
    }
  };

  const handleCleanKeycloak = async () => {
    sessionStorage.removeItem(SSO_PENDING_KEY);
    logoutUser();

    if (canKeycloak) {
      await authClient.logout(
        { redirectUri: window.location.href.replace('#survey-1', '') },
      );
    }
  };

  const keycloakValue = useMemo(() => ({
    keycloack: authClient,
    loginKeycloak: handleLoginKeycloak,
    logoutKeycloak: handleLogoutKeycloak,
    cleanKeycloak: handleCleanKeycloak,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [authClient]);

  return (
    <KeycloakContext.Provider value={keycloakValue}>
      {canKeycloak ? (
        <ReactKeycloakProvider
          authClient={authClient}
          onTokens={handleToken}
          initOptions={{
            onLoad: 'login-required',
            checkLoginIframe: false,
            pkceMethod: 'S256',
            responseMode: 'query',
          }}
        >
          {children}
        </ReactKeycloakProvider>
      ) : children}

    </KeycloakContext.Provider>
  );
};
export default KeycloakProvider;
